import React, { useState, useEffect, useMemo } from 'react';
import { UserPlus, Search, Pencil, UserX, UserCheck, X, Check, AlertTriangle, Loader2 } from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const authFetch = (url, options = {}) => {
  const token = localStorage.getItem('auth_token');
  return fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(options.headers || {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
};

// Same April-1st cycle logic used throughout the rest of the app
// (vacationUtils.getLeaveYear) — kept local here to avoid a cross-import,
// since this is the only place in this file that needs it.
const getCurrentCycleYear = () => {
  const now = new Date();
  return now.getMonth() < 3 ? now.getFullYear() - 1 : now.getFullYear();
};

const EMPTY_NEW_USER = {
  firstName: '',
  lastName: '',
  username: '',
  email: '',
  departmentId: '',
  managerId: '',
  yearlyDays: {}, // populated per-render with { [currentYear]: 25, [currentYear+1]: 25 }
  gender: '',
};

const UserManagementView = ({ currentUser, departments = [], onReloadUsers }) => {
  const [allUsers, setAllUsers] = useState([]);
  const [annualBalances, setAnnualBalances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('active'); // 'active' | 'inactive' | 'all'

  const [showAddForm, setShowAddForm] = useState(false);
  const [newUser, setNewUser] = useState(EMPTY_NEW_USER);
  const [addError, setAddError] = useState('');
  const [addSubmitting, setAddSubmitting] = useState(false);

  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState({ name: '', email: '', yearlyDays: {} });
  const [editOriginal, setEditOriginal] = useState({ name: '', email: '', yearlyDays: {} });
  const [editError, setEditError] = useState('');
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [editConfirmTarget, setEditConfirmTarget] = useState(null); // { userId, changes: [{ label, from, to }] }

  const [confirmAction, setConfirmAction] = useState(null); // { type: 'deactivate'|'reactivate', user }
  const [confirmSubmitting, setConfirmSubmitting] = useState(false);

  const [toast, setToast] = useState('');

  const currentCycleYear = getCurrentCycleYear();
  const nextCycleYear = currentCycleYear + 1;

  const editableYears = [currentCycleYear, nextCycleYear];

  const getBalanceForYear = (userId, year) => {
    const row = annualBalances.find(b => Number(b.userId || b.user_id) === Number(userId));
    if (!row) return 0;
    const key = `balance_${year}`;
    return row[key] !== undefined ? Number(row[key]) : 0;
  };

  const loadUsers = async () => {
    setLoading(true);
    setLoadError('');
    try {
      const [usersRes, balancesRes] = await Promise.all([
        authFetch(`${API_BASE_URL}/users/all`),
        authFetch(`${API_BASE_URL}/annual-balances`),
      ]);
      const usersData = await usersRes.json();
      const balancesData = await balancesRes.json();

      if (!usersRes.ok) {
        setLoadError(usersData.error || 'Αποτυχία φόρτωσης χρηστών');
        setAllUsers([]);
      } else {
        setAllUsers(usersData);
      }
      setAnnualBalances(Array.isArray(balancesData) ? balancesData : []);
    } catch (err) {
      setLoadError('Σφάλμα σύνδεσης. Παρακαλώ δοκιμάστε ξανά.');
      setAllUsers([]);
      setAnnualBalances([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 3000);
  };

  const refreshEverywhere = async () => {
    await loadUsers();
    if (onReloadUsers) await onReloadUsers();
  };

  const filteredUsers = useMemo(() => {
    return allUsers.filter(u => {
      const isActive = u.is_active === 1 || u.is_active === true;
      if (statusFilter === 'active' && !isActive) return false;
      if (statusFilter === 'inactive' && isActive) return false;
      if (!searchTerm) return true;
      const term = searchTerm.toLowerCase();
      return (u.name || '').toLowerCase().includes(term) ||
        (u.username || '').toLowerCase().includes(term) ||
          (u.email || '').toLowerCase().includes(term);
    });
  }, [allUsers, statusFilter, searchTerm]);

  const activeCount = allUsers.filter(u => u.is_active === 1 || u.is_active === true).length;
  const inactiveCount = allUsers.filter(u => !(u.is_active === 1 || u.is_active === true)).length;

  const availableManagers = useMemo(() => {
    return allUsers
      .filter(u => u.role === 'manager' && (u.is_active === 1 || u.is_active === true))
      .sort((a, b) => (a.name || '').localeCompare(b.name || '', 'el'));
  }, [allUsers]);

  // ─── Add user ───
    const handleAddSubmit = async () => {
    setAddError('');

    if (!newUser.firstName.trim()) { setAddError('Το όνομα είναι υποχρεωτικό'); return; }
    if (!newUser.lastName.trim()) { setAddError('Το επώνυμο είναι υποχρεωτικό'); return; }
    if (!newUser.username.trim()) { setAddError('Το όνομα χρήστη είναι υποχρεωτικό'); return; }
    if (!newUser.departmentId) { setAddError('Επιλέξτε τμήμα'); return; }

    const fullName = `${newUser.firstName.trim()} ${newUser.lastName.trim()}`;

    setAddSubmitting(true);
    try {
      const res = await authFetch(`${API_BASE_URL}/users`, {
        method: 'POST',
        body: JSON.stringify({
          name: fullName,
          username: newUser.username.trim(),
          email: newUser.email.trim(),
          departmentId: Number(newUser.departmentId),
          managerId: newUser.managerId ? Number(newUser.managerId) : null,
          yearlyBalances: newUser.yearlyDays,
          gender: newUser.gender || null,
        }),
      });
      const data = await res.json();
      if (data.success) {
        setShowAddForm(false);
        setNewUser({ ...EMPTY_NEW_USER, yearlyDays: { [currentCycleYear]: 25, [nextCycleYear]: 25 } });
        showToast('Ο χρήστης δημιουργήθηκε επιτυχώς');
        await refreshEverywhere();
      } else {
        setAddError(data.error || 'Αποτυχία δημιουργίας χρήστη');
      }
    } catch (err) {
      setAddError('Σφάλμα σύνδεσης. Παρακαλώ δοκιμάστε ξανά.');
    } finally {
      setAddSubmitting(false);
    }
  };

  // ─── Edit user ───
  const startEdit = (user) => {
    setEditingId(user.id);
    const initial = {
      name: user.name || '',
      email: user.email || '',
      yearlyDays: {
        [currentCycleYear]: getBalanceForYear(user.id, currentCycleYear),
        [nextCycleYear]: getBalanceForYear(user.id, nextCycleYear),
      },
    };
    setEditDraft(initial);
    setEditOriginal(initial);
    setEditError('');
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditError('');
  };

  const submitEdit = (userId) => {
    setEditError('');
    if (!editDraft.name.trim()) { setEditError('Το όνομα δεν μπορεί να είναι κενό'); return; }

    for (const year of editableYears) {
      const days = Number(editDraft.yearlyDays[year]);
      if (Number.isNaN(days) || days < 0) {
        setEditError(`Μη έγκυρος αριθμός ημερών για το ${year}`);
        return;
      }
    }

    const changes = [];

    if (editDraft.name.trim() !== editOriginal.name) {
      changes.push({ label: 'Όνομα', from: editOriginal.name || '—', to: editDraft.name.trim() });
    }
    if (editDraft.email.trim() !== editOriginal.email) {
      changes.push({ label: 'Email', from: editOriginal.email || '—', to: editDraft.email.trim() || '—' });
    }
    for (const year of editableYears) {
      const fromVal = Number(editOriginal.yearlyDays[year] ?? 0);
      const toVal = Number(editDraft.yearlyDays[year]);
      if (fromVal !== toVal) {
        changes.push({ label: `Ημέρες Άδειας ${year}`, from: String(fromVal), to: String(toVal) });
      }
    }

    if (changes.length === 0) {
      setEditingId(null);
      return;
    }

    setEditConfirmTarget({ userId, changes });
  };

  const confirmEditSave = async () => {
    if (!editConfirmTarget) return;
    const { userId } = editConfirmTarget;

    setEditSubmitting(true);
    try {
      const [profileRes, balanceRes] = await Promise.all([
        authFetch(`${API_BASE_URL}/users/${userId}`, {
          method: 'PUT',
          body: JSON.stringify({
            name: editDraft.name.trim(),
            email: editDraft.email.trim(),
          }),
        }),
        authFetch(`${API_BASE_URL}/users/${userId}/balances`, {
          method: 'PUT',
          body: JSON.stringify({ yearlyBalances: editDraft.yearlyDays }),
        }),
      ]);
      const profileData = await profileRes.json();
      const balanceData = await balanceRes.json();

      if (profileData.success && balanceData.success) {
        setEditingId(null);
        setEditConfirmTarget(null);
        showToast('Οι αλλαγές αποθηκεύτηκαν');
        await refreshEverywhere();
      } else {
        setEditConfirmTarget(null);
        setEditError(profileData.error || balanceData.error || 'Αποτυχία ενημέρωσης');
      }
    } catch (err) {
      setEditConfirmTarget(null);
      setEditError('Σφάλμα σύνδεσης. Παρακαλώ δοκιμάστε ξανά.');
    } finally {
      setEditSubmitting(false);
    }
  };

  // ─── Deactivate / reactivate ───
  const runConfirmedAction = async () => {
    if (!confirmAction) return;
    const { type, user } = confirmAction;
    setConfirmSubmitting(true);
    try {
      const endpoint = type === 'deactivate' ? 'deactivate' : 'reactivate';
      const res = await authFetch(`${API_BASE_URL}/users/${user.id}/${endpoint}`, { method: 'PUT' });
      const data = await res.json();
      if (data.success) {
        showToast(type === 'deactivate' ? 'Ο χρήστης απενεργοποιήθηκε' : 'Ο χρήστης επανενεργοποιήθηκε');
        setConfirmAction(null);
        await refreshEverywhere();
      } else {
        showToast(data.error || 'Αποτυχία ενέργειας');
        setConfirmAction(null);
      }
    } catch (err) {
      showToast('Σφάλμα σύνδεσης. Παρακαλώ δοκιμάστε ξανά.');
      setConfirmAction(null);
    } finally {
      setConfirmSubmitting(false);
    }
  };

  const getDept = (deptId) => departments.find(d => d.id === deptId);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-semibold text-gray-800 dark:text-jewel-text">Διαχείριση Χρηστών</h2>
          <p className="text-xs text-gray-500 dark:text-jewel-text-muted mt-1">
            Προσθήκη, επεξεργασία και απενεργοποίηση λογαριασμών υπαλλήλων
          </p>
        </div>
        <button
          onClick={() => {
            setNewUser({ ...EMPTY_NEW_USER, yearlyDays: { [currentCycleYear]: 25, [nextCycleYear]: 25 } });
            setShowAddForm(true);
            setAddError('');
          }}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 dark:bg-jewel-forest hover:bg-blue-700 dark:hover:bg-jewel-forest-tint text-white text-sm font-semibold rounded-lg transition-colors"
        >
          <UserPlus className="h-4 w-4" />
          Νέος Χρήστης
        </button>
      </div>
      {toast && (
        <div className="fixed top-4 right-4 z-50 bg-green-600 text-white text-sm font-medium px-4 py-2.5 rounded-lg shadow-lg flex items-center gap-2 animate-in fade-in slide-in-from-top-2">
          <Check className="h-4 w-4" />
          {toast}
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white dark:bg-jewel-bg p-4 rounded-lg border border-gray-200 dark:border-jewel-border">
          <p className="text-[10px] font-bold text-gray-400 dark:text-jewel-text-muted uppercase">Ενεργοί</p>
          <p className="text-2xl font-bold text-green-600 dark:text-green-400">{activeCount}</p>
        </div>
        <div className="bg-white dark:bg-jewel-bg p-4 rounded-lg border border-gray-200 dark:border-jewel-border">
          <p className="text-[10px] font-bold text-gray-400 dark:text-jewel-text-muted uppercase">Ανενεργοί</p>
          <p className="text-2xl font-bold text-gray-500 dark:text-jewel-text-muted">{inactiveCount}</p>
        </div>
        <div className="bg-white dark:bg-jewel-bg p-4 rounded-lg border border-gray-200 dark:border-jewel-border">
          <p className="text-[10px] font-bold text-gray-400 dark:text-jewel-text-muted uppercase">Σύνολο</p>
          <p className="text-2xl font-bold text-gray-800 dark:text-jewel-text">{allUsers.length}</p>
        </div>
      </div>
      <div className="bg-white dark:bg-jewel-bg p-4 rounded-xl border border-gray-200 dark:border-jewel-border flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 dark:text-jewel-text-muted" />
          <input
            type="text"
            placeholder="Αναζήτηση με όνομα, username ή email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 dark:border-jewel-border bg-white dark:bg-jewel-surface text-gray-900 dark:text-jewel-text rounded-lg outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div className="flex gap-2">
          {[
            { id: 'active', label: 'Ενεργοί' },
            { id: 'inactive', label: 'Ανενεργοί' },
            { id: 'all', label: 'Όλοι' },
          ].map(opt => (
            <button
              key={opt.id}
              onClick={() => setStatusFilter(opt.id)}
              className={`px-3 py-2 text-xs font-bold rounded-lg border-2 transition-all whitespace-nowrap ${
                statusFilter === opt.id
                  ? 'border-blue-500 dark:border-jewel-teal bg-blue-50 dark:bg-jewel-teal/20 text-blue-700 dark:text-jewel-teal-tint'
                  : 'border-gray-200 dark:border-jewel-border text-gray-500 dark:text-jewel-text-muted hover:border-gray-300 dark:hover:border-jewel-text-muted'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-gray-400 dark:text-jewel-text-muted" />
        </div>
      ) : loadError ? (
        <div className="text-center py-12 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <p className="text-sm text-red-600 dark:text-red-400">{loadError}</p>
        </div>
      ) : filteredUsers.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 dark:bg-jewel-surface rounded-lg border-2 border-dashed border-gray-300 dark:border-jewel-border">
          <p className="text-sm text-gray-400 dark:text-jewel-text-muted">Δεν βρέθηκαν χρήστες.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredUsers.map(user => {
            const isActive = user.is_active === 1 || user.is_active === true;
            const dept = getDept(user.department_id || user.departmentId);
            const isEditing = editingId === user.id;

            return (
              <div
                key={user.id}
                className={`bg-white dark:bg-jewel-bg border rounded-xl p-4 transition-colors ${
                  isActive ? 'border-gray-200 dark:border-jewel-border' : 'border-gray-200 dark:border-jewel-border opacity-60'
                }`}
              >
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex items-center gap-3 min-w-[220px]">
                    <div
                      className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-xs flex-shrink-0"
                      style={{ backgroundColor: dept?.color || '#9CA3AF' }}
                    >
                      {(user.name || '?').split(' ').map(n => n[0]).join('').toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-gray-900 dark:text-jewel-text truncate">{user.name}</p>
                        {!isActive && (
                          <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-gray-200 dark:bg-jewel-surface text-gray-500 dark:text-jewel-text-muted flex-shrink-0">
                            Ανενεργός
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 dark:text-jewel-text-muted truncate">{user.username} · {dept?.name || 'Χωρίς τμήμα'}</p>
                    </div>
                  </div>
                  {!isEditing && (
                    <div className="flex items-center gap-4 text-sm ml-auto">
                      <div className="text-right">
                        <p className="text-[10px] text-gray-400 dark:text-jewel-text-muted uppercase font-bold">Email</p>
                        <p className="text-gray-700 dark:text-jewel-text truncate max-w-[180px]">{user.email || '—'}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-[10px] text-gray-400 dark:text-jewel-text-muted uppercase font-bold">Ημέρες {currentCycleYear}</p>
                        <p className="text-gray-700 dark:text-jewel-text font-bold">{getBalanceForYear(user.id, currentCycleYear)}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-[10px] text-gray-400 dark:text-jewel-text-muted uppercase font-bold">Ημέρες {nextCycleYear}</p>
                        <p className="text-gray-700 dark:text-jewel-text font-bold">{getBalanceForYear(user.id, nextCycleYear)}</p>
                      </div>
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        <button
                          onClick={() => startEdit(user)}
                          title="Επεξεργασία"
                          className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-jewel-surface text-gray-500 dark:text-jewel-text-muted transition-colors"
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                        {isActive ? (
                          <button
                            onClick={() => setConfirmAction({ type: 'deactivate', user })}
                            title="Απενεργοποίηση"
                            className="p-2 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/30 text-red-500 dark:text-red-400 transition-colors"
                          >
                            <UserX className="h-4 w-4" />
                          </button>
                        ) : (
                          <button
                            onClick={() => setConfirmAction({ type: 'reactivate', user })}
                            title="Επανενεργοποίηση"
                            className="p-2 rounded-lg hover:bg-green-50 dark:hover:bg-green-900/30 text-green-600 dark:text-green-400 transition-colors"
                          >
                            <UserCheck className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                {isEditing && (
                  <div className="mt-4 pt-4 border-t border-gray-100 dark:border-jewel-border space-y-3">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="text-[10px] font-bold text-gray-400 dark:text-jewel-text-muted uppercase block mb-1">Όνομα</label>
                        <input
                          type="text"
                          value={editDraft.name}
                          onChange={(e) => setEditDraft({ ...editDraft, name: e.target.value })}
                          className="w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-jewel-border bg-white dark:bg-jewel-surface text-gray-900 dark:text-jewel-text rounded-lg outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="text-[10px] font-bold text-gray-400 dark:text-jewel-text-muted uppercase block mb-1">Email</label>
                        <input
                          type="email"
                          value={editDraft.email}
                          onChange={(e) => setEditDraft({ ...editDraft, email: e.target.value })}
                          className="w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-jewel-border bg-white dark:bg-jewel-surface text-gray-900 dark:text-jewel-text rounded-lg outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      {editableYears.map(year => (
                        <div key={year}>
                          <label className="text-[10px] font-bold text-gray-400 dark:text-jewel-text-muted uppercase block mb-1">Ημέρες Άδειας {year}</label>
                          <input
                            type="number"
                            min="0"
                            value={editDraft.yearlyDays[year] ?? ''}
                            onChange={(e) => setEditDraft({
                              ...editDraft,
                              yearlyDays: { ...editDraft.yearlyDays, [year]: e.target.value },
                            })}
                            className="w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-jewel-border bg-white dark:bg-jewel-surface text-gray-900 dark:text-jewel-text rounded-lg outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                      ))}
                    </div>
                    {editError && <p className="text-xs text-red-500 dark:text-red-400 font-medium">{editError}</p>}
                    <div className="flex gap-2">
                      <button
                        onClick={() => submitEdit(user.id)}
                        disabled={editSubmitting}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 dark:bg-jewel-forest hover:bg-blue-700 dark:hover:bg-jewel-forest-tint disabled:opacity-50 text-white text-xs font-bold rounded-lg transition-colors"
                      >
                        {editSubmitting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                        Αποθήκευση
                      </button>
                      <button
                        onClick={cancelEdit}
                        disabled={editSubmitting}
                        className="px-3 py-1.5 border border-gray-300 dark:border-jewel-border text-gray-600 dark:text-jewel-text-muted text-xs font-bold rounded-lg hover:bg-gray-50 dark:hover:bg-jewel-surface transition-colors"
                      >
                        Ακύρωση
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      {/* ─── Add user modal ─── */}
      {showAddForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-[100]">
          <div className="bg-white dark:bg-jewel-bg rounded-2xl shadow-2xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-jewel-text">Νέος Χρήστης</h3>
              <button onClick={() => setShowAddForm(false)} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-jewel-surface">
                <X className="h-5 w-5 text-gray-400 dark:text-jewel-text-muted" />
              </button>
            </div>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-gray-700 dark:text-jewel-text block mb-1">Όνομα *</label>
                  <input
                    type="text"
                    value={newUser.firstName}
                    onChange={(e) => setNewUser({ ...newUser, firstName: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-jewel-border bg-white dark:bg-jewel-surface text-gray-900 dark:text-jewel-text rounded-lg outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-700 dark:text-jewel-text block mb-1">Επώνυμο *</label>
                  <input
                    type="text"
                    value={newUser.lastName}
                    onChange={(e) => setNewUser({ ...newUser, lastName: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-jewel-border bg-white dark:bg-jewel-surface text-gray-900 dark:text-jewel-text rounded-lg outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-700 dark:text-jewel-text block mb-1">Όνομα Χρήστη (username) *</label>
                <input
                  type="text"
                  value={newUser.username}
                  onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-jewel-border bg-white dark:bg-jewel-surface text-gray-900 dark:text-jewel-text rounded-lg outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-700 dark:text-jewel-text block mb-1">Email</label>
                <input
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-jewel-border bg-white dark:bg-jewel-surface text-gray-900 dark:text-jewel-text rounded-lg outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-700 dark:text-jewel-text block mb-1">Τμήμα *</label>
                <select
                  value={newUser.departmentId}
                  onChange={(e) => setNewUser({ ...newUser, departmentId: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-jewel-border bg-white dark:bg-jewel-surface text-gray-900 dark:text-jewel-text rounded-lg outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Επιλέξτε...</option>
                  {departments.map(d => (
                    <option key={d.id} value={d.id}>{d.name}</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {editableYears.map(year => (
                  <div key={year}>
                    <label className="text-xs font-semibold text-gray-700 dark:text-jewel-text block mb-1">Ημέρες Άδειας {year}</label>
                    <input
                      type="number"
                      min="0"
                      value={newUser.yearlyDays[year] ?? ''}
                      onChange={(e) => setNewUser({
                        ...newUser,
                        yearlyDays: { ...newUser.yearlyDays, [year]: e.target.value },
                      })}
                      className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-jewel-border bg-white dark:bg-jewel-surface text-gray-900 dark:text-jewel-text rounded-lg outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                ))}
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-700 dark:text-jewel-text block mb-1">Διαχειριστής (Manager)</label>
                <select
                  value={newUser.managerId}
                  onChange={(e) => setNewUser({ ...newUser, managerId: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-jewel-border bg-white dark:bg-jewel-surface text-gray-900 dark:text-jewel-text rounded-lg outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Χωρίς ανάθεση</option>
                  {availableManagers.map(m => (
                    <option key={m.id} value={m.id}>{m.name}</option>
                  ))}
                </select>
              </div>
              {addError && <p className="text-xs text-red-500 dark:text-red-400 font-medium">{addError}</p>}
            </div>
            <div className="flex gap-3 mt-5">
              <button
                onClick={() => setShowAddForm(false)}
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-jewel-border text-gray-600 dark:text-jewel-text-muted rounded-lg hover:bg-gray-50 dark:hover:bg-jewel-surface text-sm font-semibold transition-colors"
              >
                Ακύρωση
              </button>
              <button
                onClick={handleAddSubmit}
                disabled={addSubmitting}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 dark:bg-jewel-forest hover:bg-blue-700 dark:hover:bg-jewel-forest-tint disabled:opacity-50 text-white text-sm font-bold rounded-lg transition-colors"
              >
                {addSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />}
                Δημιουργία
              </button>
            </div>
          </div>
        </div>
      )}
      {/* ─── Confirm edit (name/email/days) ─── */}
      {editConfirmTarget && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-[100]">
          <div className="bg-white dark:bg-jewel-bg rounded-2xl shadow-2xl w-full max-w-md p-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="h-5 w-5 text-amber-500" />
              </div>
              <h3 className="text-base font-semibold text-gray-900 dark:text-jewel-text">Επιβεβαίωση Αλλαγών</h3>
            </div>
            <p className="text-sm text-gray-600 dark:text-jewel-text-muted mb-4">
              Πρόκειται να αλλάξετε τα παρακάτω στοιχεία. Παρακαλώ ελέγξτε πριν συνεχίσετε:
            </p>
            <div className="space-y-2 mb-5 max-h-64 overflow-y-auto">
              {editConfirmTarget.changes.map((change, i) => (
                <div key={i} className="bg-gray-50 dark:bg-jewel-surface rounded-lg px-3 py-2.5 border border-gray-100 dark:border-jewel-border">
                  <p className="text-[10px] font-bold text-gray-400 dark:text-jewel-text-muted uppercase mb-1">{change.label}</p>
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-red-500 dark:text-red-400 line-through truncate">{change.from}</span>
                    <span className="text-gray-300 dark:text-jewel-text-muted flex-shrink-0">→</span>
                    <span className="text-green-600 dark:text-green-400 font-semibold truncate">{change.to}</span>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setEditConfirmTarget(null)}
                disabled={editSubmitting}
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-jewel-border text-gray-600 dark:text-jewel-text-muted rounded-lg hover:bg-gray-50 dark:hover:bg-jewel-surface text-sm font-semibold transition-colors"
              >
                Επιστροφή
              </button>
              <button
                onClick={confirmEditSave}
                disabled={editSubmitting}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 dark:bg-jewel-forest hover:bg-blue-700 dark:hover:bg-jewel-forest-tint disabled:opacity-50 text-white text-sm font-bold rounded-lg transition-colors"
              >
                {editSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                Επιβεβαίωση
              </button>
            </div>
          </div>
        </div>
      )}
      {/* ─── Confirm deactivate/reactivate ─── */}
      {confirmAction && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-[100]">
          <div className="bg-white dark:bg-jewel-bg rounded-2xl shadow-2xl w-full max-w-sm p-6">
            <div className="flex items-center gap-3 mb-3">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                confirmAction.type === 'deactivate' ? 'bg-red-100 dark:bg-red-900/30' : 'bg-green-100 dark:bg-green-900/30'
              }`}>
                <AlertTriangle className={`h-5 w-5 ${confirmAction.type === 'deactivate' ? 'text-red-500' : 'text-green-600'}`} />
              </div>
              <h3 className="text-base font-semibold text-gray-900 dark:text-jewel-text">
                {confirmAction.type === 'deactivate' ? 'Απενεργοποίηση Χρήστη' : 'Επανενεργοποίηση Χρήστη'}
              </h3>
            </div>
            <p className="text-sm text-gray-600 dark:text-jewel-text-muted mb-5">
              {confirmAction.type === 'deactivate' ? (
                <>Ο χρήστης <strong className="text-gray-800 dark:text-jewel-text">{confirmAction.user.name}</strong> δεν θα εμφανίζεται πλέον στην εφαρμογή. Το ιστορικό αδειών του παραμένει αμετάβλητο και μπορεί να επανενεργοποιηθεί ανά πάσα στιγμή.</>
              ) : (
                <>Ο χρήστης <strong className="text-gray-800 dark:text-jewel-text">{confirmAction.user.name}</strong> θα εμφανιστεί ξανά στην εφαρμογή κανονικά.</>
              )}
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setConfirmAction(null)}
                disabled={confirmSubmitting}
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-jewel-border text-gray-600 dark:text-jewel-text-muted rounded-lg hover:bg-gray-50 dark:hover:bg-jewel-surface text-sm font-semibold transition-colors"
              >
                Ακύρωση
              </button>
              <button
                onClick={runConfirmedAction}
                disabled={confirmSubmitting}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 text-white text-sm font-bold rounded-lg transition-colors disabled:opacity-50 ${
                  confirmAction.type === 'deactivate' ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'
                }`}
              >
                {confirmSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                Επιβεβαίωση
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagementView;